import React, { useRef, useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import useFetch from "../../../services/functions/useFetch";
import { useUser, UserRole } from "../../../services/UserContext";

const USER_REGEX = /^[a-zA-Z][a-zA-Z0-9-_]{3,23}$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!?@#€$%]).{8,24}$/;

const Register: React.FC = () => {
  const userRef = useRef<HTMLInputElement>(null);
  const errRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const { role } = useUser();

  const [username, setUsername] = useState<string>("");
  const [validName, setValidName] = useState<boolean>(false);
  const [userFocus, setUserFocus] = useState<boolean>(false);

  const [password, setPassword] = useState<string>("");
  const [validPassword, setValidPassword] = useState<boolean>(false);
  const [passwordFocus, setPasswordFocus] = useState<boolean>(false);

  const [matchPassword, setMatchPassword] = useState<string>("");
  const [validMatchPassword, setValidMatchPassword] = useState<boolean>(false);
  const [matchPasswordFocus, setMatchPasswordFocus] = useState<boolean>(false);

  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  const [errorMsg, setErrorMsg] = useState<string>("");

  const { fetchData } = useFetch();

  useEffect(() => {
    if (userRef.current) {
      userRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const result = USER_REGEX.test(username);
    setValidName(result);
  }, [username]);

  useEffect(() => {
    const result = PWD_REGEX.test(password);
    setValidPassword(result);
    const match = password === matchPassword;
    setValidMatchPassword(match);
  }, [password, matchPassword]);

  useEffect(() => {
    setErrorMsg("");
  }, [username, password, matchPassword]);

  // Redirect unauthorized users
  useEffect(() => {
    if (role !== UserRole.ADMIN) {
      setErrorMsg("Keine Zugriffsberechtigung!");
    }
  }, [role]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const v1 = USER_REGEX.test(username);
    const v2 = PWD_REGEX.test(password);
    if (!v1 || !v2) {
      setErrorMsg("Ungültiger Eintrag");
      return;
    }

    fetchData("/add/user", "POST", {
      body: {
        username: username,
        password: password,
        isAdmin: isAdmin,
      },
      doOnSuccess: () => {
        navigate("/");
      },
      doOnError: (error) => {
        console.error("Failed to register user:", error);
        setErrorMsg("Fehler beim Anlegen des Users: " + error);
      },
    });
  };

  if (role !== UserRole.ADMIN) {
    return <p className="form-error">{errorMsg}</p>;
  }

  return (
    <>
      <section className="section-container">
        <div className="content-wrapper">
          <div className="form-container">
            <h1 className="form-title">Registrierung</h1>
            <p
              ref={errRef}
              className={errorMsg ? "register__error" : "register__offscreen"}
              aria-live="assertive"
            >
              {errorMsg}
            </p>
            <form className="form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Benutzername
                </label>
                <input
                  type="username"
                  name="username"
                  id="username"
                  className="form-input"
                  placeholder="MaxMustermann"
                  ref={userRef}
                  autoComplete="off"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  aria-invalid={validName ? "false" : "true"}
                  aria-describedby="uidnote"
                  onFocus={() => setUserFocus(true)}
                  onBlur={() => setUserFocus(false)}
                  required
                />
                <p
                  id="uidnote"
                  className={
                    userFocus && username && !validName
                      ? "register__instructions"
                      : "register__offscreen"
                  }
                >
                  4-24 Zeichen
                  <br />
                  Muss mit einem Buchstaben beginnen
                  <br />
                  Buchstaben, Zahlen, Unterstriche, Bindestriche erlaubt
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Passwort
                </label>
                <input
                  type="password"
                  name="password"
                  id="password"
                  className="form-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={validPassword ? "false" : "true"}
                  aria-describedby="pwdnote"
                  onFocus={() => setPasswordFocus(true)}
                  onBlur={() => setPasswordFocus(false)}
                  required
                />
                <p
                  id="pwdnote"
                  className={
                    passwordFocus && !validPassword
                      ? "register__instructions"
                      : "register__offscreen"
                  }
                >
                  8-24 Zeichen
                  <br />
                  Muss Groß- und Kleinbuchstabend enthalten
                  <br />
                  Muss Zahlen und Sonderzeichen enthalten
                  <br />
                  Erlaube Sonderzeichen:
                  <span aria-label="ausrufezeichen">!</span>
                  <span aria-label="fragezeichen">?</span>
                  <span aria-label="at symbol">@</span>
                  <span aria-label="raute">#</span>
                  <span aria-label="euro">€</span>
                  <span aria-label="dollar">$</span>
                  <span aria-label="prozent">%</span>
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Passwort bestätigen
                </label>
                <input
                  type="password"
                  id="password-confirm"
                  className="form-input"
                  placeholder="••••••••"
                  value={matchPassword}
                  onChange={(e) => setMatchPassword(e.target.value)}
                  aria-invalid={validMatchPassword ? "false" : "true"}
                  aria-describedby="confirmnote"
                  onFocus={() => setMatchPasswordFocus(true)}
                  onBlur={() => setMatchPasswordFocus(false)}
                  required
                />
                <p
                  id="confirmnote"
                  className={
                    matchPasswordFocus && !validMatchPassword
                      ? "register__instructions"
                      : "register__offscreen"
                  }
                >
                  Passwörter stimmen nicht überein
                </p>
              </div>

              <label htmlFor="adminCheckbox" className="form-label">
                Admin
              </label>
              <input
                type="checkbox"
                id="adminCheckbox"
                name="admin"
                checked={isAdmin}
                onChange={(e) => setIsAdmin(e.target.checked)}
                className="form-checkbox"
              />

              {isAdmin && (
                <p className="register__error">
                  Achtung: Admins können neue Benutzer anlegen!
                </p>
              )}

              <button
                type="submit"
                className="form-button"
                disabled={
                  !validName || !validPassword || !validMatchPassword
                    ? true
                    : false
                }
              >
                Registrieren
              </button>
            </form>

            {/* 
                <div className="sign-up-switch">
                  <p className="sign-up-text">Bereits registriert?</p>
                  <Link to="/login" className="sign-up-link">
                    Anmeldung
                  </Link>
                </div>
                */}
          </div>
        </div>
      </section>
    </>
  );
};

export default Register;
